import logger from './logger';

// The business operates in Malaysia; all dates are stored and displayed in this timezone.
const MALAYSIA_TZ = 'Asia/Kuala_Lumpur';

/**
 * Parse a backend datetime value into a Date, unambiguously.
 *
 * Backend timestamps are naive Malaysia-time strings ("YYYY-MM-DD HH:MM:SS", no offset).
 * `new Date()` would parse those in the browser's local timezone, so we append the +08:00
 * offset when no timezone marker is present. Strings that already carry an offset (ISO with
 * `Z` or `±hh:mm`, e.g. client-generated `toISOString()`) are parsed as-is.
 */
const parseAsMalaysia = (value: string | Date): Date => {
  if (value instanceof Date) return value;
  const trimmed = value.trim();
  const hasTimezone = /([zZ])|([+-]\d{2}:?\d{2})$/.test(trimmed);
  const normalized = hasTimezone ? trimmed : `${trimmed.replace(' ', 'T')}+08:00`;
  return new Date(normalized);
};

/**
 * Format a date for display in Malaysia time (UTC+8), regardless of the viewer's browser tz.
 *
 * @param dateString - Backend datetime string (naive MYT or ISO-with-offset) or Date object
 * @param format - Format style ('short', 'medium', 'long', 'full', or 'time')
 * @returns Formatted date string in Asia/Kuala_Lumpur
 */
export const formatDate = (
  dateString: string | Date,
  format: 'short' | 'medium' | 'long' | 'full' | 'time' = 'medium'
): string => {
  try {
    const date = parseAsMalaysia(dateString);

    if (format === 'time') {
      return new Intl.DateTimeFormat('en-MY', {
        timeZone: MALAYSIA_TZ,
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    }

    const options: Intl.DateTimeFormatOptions = {
      timeZone: MALAYSIA_TZ,
      year: 'numeric',
      month: format === 'short' ? 'numeric' : format === 'medium' ? 'short' : 'long',
      day: 'numeric'
    };

    if (format === 'long' || format === 'full') {
      options.hour = 'numeric';
      options.minute = 'numeric';
    }

    if (format === 'full') {
      options.second = 'numeric';
      options.timeZoneName = 'short';
    }

    return new Intl.DateTimeFormat('en-MY', options).format(date);
  } catch (e: unknown) {
    logger.error('Error formatting date:', e);
    return String(dateString);
  }
};

/**
 * Current date/time parts in Malaysia time, for filter defaults that must not depend on the
 * viewer's browser timezone.
 *
 * @returns `{ year, month }` where month is 1-12, evaluated in Asia/Kuala_Lumpur
 */
export const getMalaysiaNow = (): { year: number; month: number; day: number } => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: MALAYSIA_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(new Date());
  const year = Number(parts.find((p) => p.type === 'year')?.value);
  const month = Number(parts.find((p) => p.type === 'month')?.value);
  const day = Number(parts.find((p) => p.type === 'day')?.value);
  return { year, month, day };
};

/**
 * Format a number as Malaysian Ringgit currency
 * 
 * @param amount - Number to format
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-MY', {
    style: 'currency',
    currency: 'MYR'
  }).format(amount);
};

/**
 * Format a phone number to a readable format
 * 
 * @param phone - Phone number string
 * @returns Formatted phone number
 */
export const formatPhoneNumber = (phone: string): string => {
  // Basic formatting for Malaysian phone numbers
  if (!phone) return '';
  
  // Remove non-numeric characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Format based on length
  if (cleaned.length === 10) {
    // Format as 01X-XXX XXXX
    return `${cleaned.substring(0, 3)}-${cleaned.substring(3, 6)} ${cleaned.substring(6)}`;
  } else if (cleaned.length === 11) {
    // Format as 01XX-XXX XXXX
    return `${cleaned.substring(0, 4)}-${cleaned.substring(4, 7)} ${cleaned.substring(7)}`;
  }
  
  // Return as is if it doesn't match expected patterns
  return phone;
};

/**
 * Truncate a string to a specified length with ellipsis
 * 
 * @param str - String to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated string
 */
export const truncateString = (str: string, maxLength: number): string => {
  if (!str) return '';
  if (str.length <= maxLength) return str;
  
  return `${str.substring(0, maxLength)}...`;
};

/**
 * Format a file size in bytes to a human-readable string
 * 
 * @param bytes - File size in bytes
 * @param decimals - Number of decimal places
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
};

/**
 * Label a commission breakdown row by its real type.
 *
 * Genuine delivery routes keep their leg labels — the last Delivery segment is the trip back
 * to base ("Return trip"). Penalties and bonuses show their actual type instead of being
 * mislabeled "Delivery"/"Return trip".
 *
 * @param seg - The commission row (must have a `type`)
 * @param idx - Its index within the order's segment list
 * @param segments - All segments for the order
 * @returns A human-readable label
 */
export const commissionSegmentLabel = (
  seg: { type: string },
  idx: number,
  segments: { type: string }[]
): string => {
  const labels: Record<string, string> = {
    Delivery: 'Delivery',
    LatePickup: 'Late Pickup',
    LateMarking: 'Late Marking',
    Bonus: 'Bonus',
  };

  if (seg.type === 'Delivery') {
    const lastDeliveryIdx = segments.reduce(
      (last, s, i) => (s.type === 'Delivery' ? i : last),
      -1
    );
    return idx === lastDeliveryIdx ? 'Return trip' : 'Delivery';
  }

  return labels[seg.type] ?? seg.type ?? 'Unknown';
};